import React from "react";
import { useField, useFormikContext } from "formik";

// material
import {
  Box,
  Container,
  FormControl,
  FormControlLabel,
  MenuItem,
  RadioGroup,
  Radio,
  Typography,
  TextField,
  Stack
} from "@mui/material";

// components
import Page from "../../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";

// ----------------------------------------------------------------------

const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
};

export default function UploadFormPage2B(props) {
  const formikProps = useFormikContext();

  const {
    formField: {
      startWritingResearchProposal,
      uploadCurrentResearchProposal,
      qualifyingExamResult,
      whenQualifyingExamination,
      proposalExaminationResult,
      whenProposalExamination,
      presentProgressResearchProject,
      subjectAreaPublished,
      manuscriptPublication,
      manuscriptId,
      journalRanking,
      conductingResearchAbroad6Months,
      answerPlaning,
      answerCountryPlan,
      highPerformanceProfessor,
      professorCollaboratorName,
      instituteProfessorCollaborator
    }
  } = props;

  const [startWritingResearchProposalField, startWritingResearchProposalMeta] =
    useField(startWritingResearchProposal);
  const [
    uploadCurrentResearchProposalField,
    uploadCurrentResearchProposalMeta
  ] = useField(uploadCurrentResearchProposal);
  const [qualifyingExamResultField, qualifyingExamResultMeta] =
    useField(qualifyingExamResult);
  const [whenQualifyingExaminationField, whenQualifyingExaminationMeta] =
    useField(whenQualifyingExamination);
  const [proposalExaminationResultField, proposalExaminationResultMeta] =
    useField(proposalExaminationResult);
  const [whenProposalExaminationField, whenProposalExaminationMeta] = useField(
    whenProposalExamination
  );
  const [
    presentProgressResearchProjectField,
    presentProgressResearchProjectMeta
  ] = useField(presentProgressResearchProject);
  const [subjectAreaPublishedField, subjectAreaPublishedMeta] =
    useField(subjectAreaPublished);
  const [manuscriptPublicationField, manuscriptPublicationMeta] = useField(
    manuscriptPublication
  );
  const [manuscriptIdField, manuscriptIdMeta] = useField(manuscriptId);
  const [journalRankingField, journalRankingMeta] = useField(journalRanking);
  const [
    conductingResearchAbroad6MonthsField,
    conductingResearchAbroad6MonthsMeta
  ] = useField(conductingResearchAbroad6Months);
  const [answerPlaningField, answerPlaningMeta] = useField(answerPlaning);
  const [answerCountryPlanField, answerCountryPlanMeta] =
    useField(answerCountryPlan);
  const [highPerformanceProfessorField, highPerformanceProfessorMeta] =
    useField(highPerformanceProfessor);
  const [professorCollaboratorNameField, professorCollaboratorNameMeta] =
    useField(professorCollaboratorName);
  const [
    instituteProfessorCollaboratorField,
    instituteProfessorCollaboratorMeta
  ] = useField(instituteProfessorCollaborator);

  return (
    <Page title="2B: Progress Report">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">
            PART: Grade/Progress/Final Report (Continue)
          </Typography>
        </Box>
        <Box sx={{ pb: 5 }}>
          <Typography variant="h5">2B: Progress Report</Typography>
        </Box>
        <Stack
          direction="column"
          justifyContent="space-evenly"
          alignItems="stretch"
          spacing={4}
        >
          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {startWritingResearchProposal.label}
            </Typography>
            <RadioGroup
              name={startWritingResearchProposalField.name}
              value={startWritingResearchProposalField.value}
              onChange={(e) => startWritingResearchProposalField.onChange(e)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
            {startWritingResearchProposalMeta.touched && (
              <span
                style={{
                  color: "#FF4842",
                  lineHeight: 1.5,
                  fontSize: "0.75rem",
                  fontFamily: "sans-serif",
                  fontWeight: 400,
                  textAlign: "left",
                  marginTop: "3px",
                  marginRight: "14px",
                  marginBottom: 0,
                  marginLeft: "14px"
                }}
              >
                {startWritingResearchProposalMeta.error}
              </span>
            )}
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {uploadCurrentResearchProposal.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={uploadCurrentResearchProposalField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `uploadCurrentResearchProposal.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                uploadCurrentResearchProposalMeta.touched &&
                  uploadCurrentResearchProposalMeta.error
              )}
              helperText={
                uploadCurrentResearchProposalMeta.touched &&
                uploadCurrentResearchProposalMeta.error
              }
            />
          </FormControl>

          <Typography>
            <strong>Note:</strong> We recommend 2nd year-students to prepare for
            their research proposal examination within semester 2 of the
            academic year.
          </Typography>

          <Typography variant="h6">Qualifying Examination Report:</Typography>

          <Stack direction={"row"} spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {qualifyingExamResult.label}
              </Typography>
              <TextField
                select
                variant="outlined"
                name={qualifyingExamResultField.name}
                value={qualifyingExamResultField.value}
                onChange={(e) => qualifyingExamResultField.onChange(e)}
                error={Boolean(
                  qualifyingExamResultMeta.touched &&
                    qualifyingExamResultMeta.error
                )}
                helperText={
                  qualifyingExamResultMeta.touched &&
                  qualifyingExamResultMeta.error
                }
              >
                <MenuItem value={"pass"}>Pass</MenuItem>
                <MenuItem value={"not_pass"}>Not pass</MenuItem>
                <MenuItem value={"have_not_done"}>Have not done</MenuItem>
              </TextField>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {whenQualifyingExamination.label}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="dd/MM/yyyy"
                  name={whenQualifyingExaminationField.name}
                  value={whenQualifyingExaminationField.value}
                  fullWidth
                  onChange={(date) => {
                    if (isValidDate(date)) {
                      formikProps.setFieldValue(
                        whenQualifyingExaminationField.name,
                        date.toISOString()
                      );
                    } else {
                      formikProps.setFieldValue(
                        whenQualifyingExaminationField.name,
                        date
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(
                        whenQualifyingExaminationMeta.touched &&
                          whenQualifyingExaminationMeta.error
                      )}
                      helperText={
                        whenQualifyingExaminationMeta.touched &&
                        whenQualifyingExaminationMeta.error
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Stack>

          <Typography variant="h6">
            Research Proposal Examination Report:
          </Typography>

          <Stack direction={"row"} spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {proposalExaminationResult.label}
              </Typography>
              <TextField
                select
                variant="outlined"
                name={proposalExaminationResultField.name}
                value={proposalExaminationResultField.value}
                onChange={(e) => proposalExaminationResultField.onChange(e)}
                error={Boolean(
                  proposalExaminationResultMeta.touched &&
                    proposalExaminationResultMeta.error
                )}
                helperText={
                  proposalExaminationResultMeta.touched &&
                  proposalExaminationResultMeta.error
                }
              >
                <MenuItem value={"pass"}>Pass</MenuItem>
                <MenuItem value={"not_pass"}>Not pass</MenuItem>
                <MenuItem value={"have_not_done"}>Have not done</MenuItem>
              </TextField>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {whenProposalExamination.label}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="dd/MM/yyyy"
                  name={whenProposalExaminationField.name}
                  value={whenProposalExaminationField.value}
                  fullWidth
                  onChange={(date) => {
                    if (isValidDate(date)) {
                      formikProps.setFieldValue(
                        whenProposalExaminationField.name,
                        date.toISOString()
                      );
                    } else {
                      formikProps.setFieldValue(
                        whenProposalExaminationField.name,
                        date
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(
                        whenProposalExaminationMeta.touched &&
                          whenProposalExaminationMeta.error
                      )}
                      helperText={
                        whenProposalExaminationMeta.touched &&
                        whenProposalExaminationMeta.error
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle1">
              {presentProgressResearchProject.label}
            </Typography>
            <RadioGroup
              name={presentProgressResearchProjectField.name}
              value={presentProgressResearchProjectField.value}
              onChange={(e) => presentProgressResearchProjectField.onChange(e)}
            >
              <FormControlLabel
                value="section_a"
                control={<Radio />}
                label="Section A: Research design / Data collection and analysis (25-50% of overall achievement)"
              />
              <FormControlLabel
                value="section_b"
                control={<Radio />}
                label="Section B: Obtaining results / Selecting journal / Writing publication (50-75% of overall achievement)"
              />
              <FormControlLabel
                value="section_c"
                control={<Radio />}
                label="Section C: Submission / Under review process (75-90% of overall achievement)"
              />
              <FormControlLabel
                value="section_d"
                control={<Radio />}
                label="Section D: Being accepted for publication (100% achievement)"
              />
              {presentProgressResearchProjectMeta.touched && (
                <span
                  style={{
                    color: "#FF4842",
                    lineHeight: 1.5,
                    fontSize: "0.75rem",
                    fontFamily: "sans-serif",
                    fontWeight: 400,
                    textAlign: "left",
                    marginTop: "3px",
                    marginRight: "14px",
                    marginBottom: 0,
                    marginLeft: "14px"
                  }}
                >
                  {presentProgressResearchProjectMeta.error}
                </span>
              )}
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {subjectAreaPublished.label}
            </Typography>
            <TextField
              multiline
              rows={5}
              name={subjectAreaPublishedField.name}
              value={subjectAreaPublishedField.value}
              onChange={(e) => subjectAreaPublishedField.onChange(e)}
              error={Boolean(
                subjectAreaPublishedMeta.touched &&
                  subjectAreaPublishedMeta.error
              )}
              helperText={
                subjectAreaPublishedMeta.touched &&
                subjectAreaPublishedMeta.error
              }
            />
            <Typography>
              ** In case of 5 yrs-program: If you select section C or section D
              for sending 01 (Sci) / Q2 (Social sci) publication. Please attach
              your manuscript and indicate manuscript ID:
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="subtitle1">
              {manuscriptPublication.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={manuscriptPublicationField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `manuscriptPublication.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                manuscriptPublicationMeta.touched &&
                  manuscriptPublicationMeta.error
              )}
              helperText={
                manuscriptPublicationMeta.touched &&
                manuscriptPublicationMeta.error
              }
            />
          </FormControl>

          <Stack direction={"row"} spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{manuscriptId.label}</Typography>
              <TextField
                name={manuscriptIdField.name}
                value={manuscriptIdField.value}
                onChange={(e) => manuscriptIdField.onChange(e)}
                error={Boolean(
                  manuscriptIdMeta.touched && manuscriptIdMeta.error
                )}
                helperText={manuscriptIdMeta.touched && manuscriptIdMeta.error}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {journalRanking.label}
              </Typography>
              <TextField
                select
                variant="outlined"
                name={journalRankingField.name}
                value={journalRankingField.value}
                onChange={(e) => journalRankingField.onChange(e)}
                error={Boolean(
                  journalRankingMeta.touched && journalRankingMeta.error
                )}
                helperText={
                  journalRankingMeta.touched && journalRankingMeta.error
                }
              >
                <MenuItem value={"q1"}>Q1</MenuItem>
                <MenuItem value={"q2"}>Q2</MenuItem>
              </TextField>
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {conductingResearchAbroad6Months.label}
            </Typography>
            <RadioGroup
              name={conductingResearchAbroad6MonthsField.name}
              value={conductingResearchAbroad6MonthsField.value}
              onChange={(e) => conductingResearchAbroad6MonthsField.onChange(e)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              {conductingResearchAbroad6MonthsMeta.touched && (
                <span
                  style={{
                    color: "#FF4842",
                    lineHeight: 1.5,
                    fontSize: "0.75rem",
                    fontFamily: "sans-serif",
                    fontWeight: 400,
                    textAlign: "left",
                    marginTop: "3px",
                    marginRight: "14px",
                    marginBottom: 0,
                    marginLeft: "14px"
                  }}
                >
                  {conductingResearchAbroad6MonthsMeta.error}
                </span>
              )}
            </RadioGroup>
          </FormControl>

          <Stack direction={"row"} spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{answerPlaning.label}</Typography>
              <TextField
                name={answerPlaningField.name}
                value={answerPlaningField.value}
                onChange={(e) => answerPlaningField.onChange(e)}
                error={Boolean(
                  answerPlaningMeta.touched && answerPlaningMeta.error
                )}
                helperText={
                  answerPlaningMeta.touched && answerPlaningMeta.error
                }
              />
              <Typography>For example: July, 2020.</Typography>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {answerCountryPlan.label}
              </Typography>
              <TextField
                name={answerCountryPlanField.name}
                value={answerCountryPlanField.value}
                onChange={(e) => answerCountryPlanField.onChange(e)}
                error={Boolean(
                  answerCountryPlanMeta.touched && answerCountryPlanMeta.error
                )}
                helperText={
                  answerCountryPlanMeta.touched && answerCountryPlanMeta.error
                }
              />
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {highPerformanceProfessor.label}
            </Typography>
            <RadioGroup
              row
              name={highPerformanceProfessorField.name}
              value={highPerformanceProfessorField.value}
              onChange={(e) => highPerformanceProfessorField.onChange(e)}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes, I have."
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No, I do not have."
              />
              <FormControlLabel
                value="noFinding"
                control={<Radio />}
                label="No, I do not have but I'm finding."
              />
            </RadioGroup>
            {highPerformanceProfessorMeta.touched && (
              <span
                style={{
                  color: "#FF4842",
                  lineHeight: 1.5,
                  fontSize: "0.75rem",
                  fontFamily: "sans-serif",
                  fontWeight: 400,
                  textAlign: "left",
                  marginTop: "3px",
                  marginRight: "14px",
                  marginBottom: 0,
                  marginLeft: "14px"
                }}
              >
                {highPerformanceProfessorMeta.error}
              </span>
            )}
          </FormControl>

          <Stack direction={"row"} spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {professorCollaboratorName.label}
              </Typography>
              <TextField
                name={professorCollaboratorNameField.name}
                value={professorCollaboratorNameField.value}
                onChange={(e) => professorCollaboratorNameField.onChange(e)}
                error={Boolean(
                  professorCollaboratorNameMeta.touched &&
                    professorCollaboratorNameMeta.error
                )}
                helperText={
                  professorCollaboratorNameMeta.touched &&
                  professorCollaboratorNameMeta.error
                }
              />
              <Typography>
                **You can ask your mentor for this issue."*
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {instituteProfessorCollaborator.label}
              </Typography>
              <TextField
                name={instituteProfessorCollaboratorField.name}
                value={instituteProfessorCollaboratorField.value}
                onChange={(e) =>
                  instituteProfessorCollaboratorField.onChange(e)
                }
                error={Boolean(
                  instituteProfessorCollaboratorMeta.touched &&
                    instituteProfessorCollaboratorMeta.error
                )}
                helperText={
                  instituteProfessorCollaboratorMeta.touched &&
                  instituteProfessorCollaboratorMeta.error
                }
              />
              <Typography>
                **You can ask your mentor for this issue.**
              </Typography>
            </FormControl>
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
}
